import type { Answers, TSuppressionProfile } from "./_schema";
import { countReportedSigns, deriveCortisolProfile } from "./cortisol-profile";
import { EN_PROFILE_COPY, type ProfileCopyPack } from "./profile-copy";

/**
 * CORTISOL-STORY deriver: the shared cortisol verdict, plus one narrow
 * exception to the rule-12a severity floor for the completer who reports
 * NONE of the eight cortisol signs.
 *
 * WHY THIS EXISTS. Rule 12a floors severity at `normal` ("EARLY DECLINE") on
 * the stated grounds that anyone who completes a symptom-motivated quiz is
 * feeling a consequence, so the result page must never absolve them. That
 * premise is sound for every quiz built on it, and it is FALSE BY
 * CONSTRUCTION for a man who has just answered "no" to all eight signs. The
 * floor made this quiz tell him he was in early decline, that stress hormones
 * were "starting to interfere with testosterone production", and that his
 * outcome was "Hormonal Recovery" -- a verdict his own answers contradict,
 * which is the single fastest way to make a diagnostic feel rigged. This quiz
 * also asks him to count the signs out loud on two separate screens, so he
 * knows exactly what he reported and would catch the contradiction.
 *
 * Operator directive 2026-09-02: tell him he is fine, then sell the next
 * level rather than a repair. So the zero-signs verdict becomes an
 * OPTIMIZATION verdict (stable, low suppression, headroom framing) instead of
 * a damage verdict. The projection chart already had the right label waiting
 * for this path -- `low` renders "BASELINE OPTIMIZATION".
 *
 * WHY IT IS A WRAPPER, NOT AN EDIT. `deriveCortisolProfile` is shared by the
 * LIVE `cortisol` quiz and its two live tracking clones (`cortisol-quiz-al`,
 * `cortisol-quiz-gl`). Editing it would silently change the verdict on three
 * surfaces taking real traffic. So the exception is composed on top of it and
 * scoped to this slug, exactly like every other angle quiz composes the base
 * quiz-v6 seams rather than editing the base (rule 11a). The base deriver and
 * its exhaustive regression lock are untouched.
 *
 * WHAT IT DOES NOT CHANGE. `rootCauses` (so the forced `cortisol-dominance`
 * primary still leads on every path, locked in cortisol-story.test.ts),
 * `qualityOfLifeImpact`, `evidence`, and `cohort` all pass through. Only the
 * four verdict fields a clean man would read as false are rewritten.
 *
 * Pure function of `answers` -- reproducible server-side from the persisted
 * submission (rule 11).
 */

/**
 * Verdict copy for the optimization path.
 *
 * `label` / `headline` come from the copy pack's existing `low` entries
 * rather than being forked here, so a future locale clone inherits them from
 * its own pack (same reason `deriveBoostProfile` takes a `copy` seam). Only
 * `explanation` and `expectedOutcome` are authored, because the pack keys
 * explanations by root cause and tone -- neither of which can express "this
 * man has no symptoms" without adding a tone to every locale pack.
 *
 * Kept near the ~110-char house median: it renders inside the fold-critical
 * assessment card (rule 11a).
 */
const OPTIMIZATION_EXPLANATION =
  "Your answers show none of the eight cortisol signs. There is nothing here to reverse, so the work is raising your ceiling.";

const OPTIMIZATION_OUTCOME = {
  label: "Performance Headroom",
  copy: "Your protocol lowers the cortisol ceiling that decides how much of your testosterone you actually get to use.",
} as const;

export function deriveCortisolStoryProfile(
  answers: Answers,
  opts: { copy?: ProfileCopyPack } = {},
): TSuppressionProfile {
  const base = deriveCortisolProfile(answers);
  if (countReportedSigns(answers) > 0) return base;

  const copy = opts.copy ?? EN_PROFILE_COPY;
  return {
    ...base,
    suppressionLevel: "low",
    declinePattern: "stable",
    assessment: {
      label: copy.assessmentLabel.low,
      headline: copy.assessmentHeadline.low,
      explanation: OPTIMIZATION_EXPLANATION,
    },
    expectedOutcome: { ...OPTIMIZATION_OUTCOME },
  };
}
